import { useTranslation } from 'react-i18next';
import queryClient from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionDto, AIActionUsageStatsDto } from '@/Domain/api/Action/dto/Action.dto';
import { ActionBox } from '@/Domain/components/Action/ActionBox/ActionBox';
import { RemoveActionModal } from '@/Domain/components/Action/RemoveAction.modal';
import { useActivateKnowledgeBaseAction } from '@/Domain/hooks/Action/KnowledgeBaseAction/useActivateKnowledgeBaseAction';
import { useDuplicateAction } from '@/Domain/hooks/Action/useDuplicateAction.hook';
import { useUser } from '@/Domain/hooks/User/useUser.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import { isQuickchatEmail } from '@/Domain/utils/isQuickchatEmail';

type Props = {
  action: ActionDto;
  stats?: AIActionUsageStatsDto;
};

export const KnowledgeBaseActionBox = ({ action, stats }: Props) => {
  const { t } = useTranslation('knowledge_base_action');
  const { toggleModal } = useModalStore();
  const { duplicateAction, isDuplicating, canDuplicate } = useDuplicateAction();
  const { user } = useUser();
  // Creating a Knowledge Base action is staff-only; copying one is another way to create.
  const canDuplicateKnowledgeBase = isQuickchatEmail(user?.email || '');

  const handleFetchAction = async () =>
    await queryClient.fetchQuery({
      queryKey: [QueryKey.actions.knowledgeBase.get, action.id],
    });

  const { handleActive: handleKnowledgeBaseActionActive } = useActivateKnowledgeBaseAction(action);

  const handleDelete = () => {
    toggleModal(`${MODAL_NAMES.REMOVE_ACTION_MODAL_NAME}_${action.id}`);
  };

  const handleEdit = async () => {
    const knowledgeBaseAction = await queryClient.ensureQueryData({
      queryKey: [QueryKey.actions.knowledgeBase.get, action.id],
    });
    toggleModal(MODAL_NAMES.KNOWLEDGE_BASE_ACTION_SHEET, { knowledgeBaseAction });
  };

  return (
    <>
      <ActionBox
        dataTestId="knowledge-base-action"
        action={action}
        stats={stats}
        onMouseEnterAction={handleFetchAction}
        onActive={handleKnowledgeBaseActionActive}
        onEdit={handleEdit}
        onDuplicate={canDuplicateKnowledgeBase ? () => duplicateAction(action.id) : undefined}
        isDuplicating={isDuplicating}
        canDuplicate={canDuplicate}
        onDelete={handleDelete}
        buttonLabel={t('button.edit')}
      />

      <RemoveActionModal actionId={action.id} />
    </>
  );
};
